import { Grid, Box, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@dextech-ai/dex-design-system';
import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg'
import Button from '@/components/Button/index'
import CardUseCase from '@/components/CardUseCase/index'
import InputPrefixIcon from '@/components/InputPrefixIcon/index'
import Portal from '@/components/Portal'
import Tabs, { Tab } from '@/components/Tabs/index'
import Toast from '@/components/Toast'
import { featureConstant } from '@/constants/configuration/feature'
import useDebounce from '@/hooks/use-debounce/index'
import useModal from '@/hooks/use-modal'
import usePage from '@/hooks/use-page'
import useToast from '@/hooks/use-toast'
import useUseCases from '@/hooks/use-use-cases'
import { NavLink, useLocation, useNavigate } from '@/lib/router-dom/index'
import PrincipalPage from '@/pages/use-cases/agent-principal/index'
import { feature } from '@/utils/feature-verifier'
import rem from '@/utils/rem'
import { orderBy } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Search  from  '@/componentsDS/Search/index'
// [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR.
// import Breadcrumbs from '@/componentsDS/Breadcrumbs/index'
import Breadcrumb from '@/dsComponents/Breadcrumb';
export const PrincipalMenuWithOutsideClicked = ({ options, onClick }) => (
	<DropdownMenu trigger="hover" placement="bottomRight">
		<DropdownMenuTrigger asChild>
			<div>
				<Button
					addClass="agent_principal__add_new_principal__button"
					full
					round
					text="Add New Principal"
					suffix={<IconChevronDown className="agent_principal__add_new_principal__icon" />}
				/>
			</div>
		</DropdownMenuTrigger>
		<DropdownMenuContent>
			{options?.map((option) => (
				<DropdownMenuItem itemKey={option.type} key={option.type} onSelect={() => onClick(option.type)}>
					<span id="principal-id">{option.text}</span>
				</DropdownMenuItem>
			))}
		</DropdownMenuContent>
	</DropdownMenu>
)

PrincipalMenuWithOutsideClicked.propTypes = {
	options: PropTypes.array,
	onClick: PropTypes.func,
}

const Page = () => {

	/*=================================================
		General
	/================================================*/

	const { currentPath } = useLocation();
	const { goTo, replace } = useNavigate()

	const statuses = {
		approved: "APPROVED",
		rejected: "REJECTED",
		revoked: "REVOKED",
		pending: "PENDING",
		notEnrolled: "NOT_ENROLLED",
		signdsa: "DSA_REQUESTED",
		informationRequired: "INFO_REQUESTED"
	}

	const toastType = {
		success: "success",
		error: "error"
	}

	useEffect(() => {
		if (currentPath === "/use-cases") {
			replace("/use-cases/enrolment")
		}
	}, [currentPath])

	const { breadcrumbsItems } = usePage({
		title: 'Use Cases',
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Use Cases',
				link: ``
			},
		]
	 })
	const [searchTerm, setSearchTerm] = useState("")
	const [searchBoxValue, setSearchBoxValue] = useState("")
	const debounceGetUseCases = useDebounce(getUseCases, "dataFiltering");

	const userType = useSelector(state => state.auth.userType)
	const profile = useSelector(state => state.auth.profile)

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { items, fetch, setItems, isAgentPrincipalEnabled, error } = useUseCases()

	async function getUseCases() {
		const useCases = searchBoxValue && searchBoxValue.length > 0 ? await fetch({ useCaseTitle: searchBoxValue }) : await fetch();
		const updatedItems = orderBy(useCases, ["name"], ['asc'])
		setItems(updatedItems)
	}

	useEffect(() => {
		if (error)
			addToastMessage(error, toastType.error)
	}, [error])

	/*=================================================
		Modal
	/================================================*/

	const { visible: visibleRemovePrincipal, show: showRemovePrincipal, hide: hideRemovePrincipal } = useModal()

	/*=================================================
		Search
	/================================================*/
	useEffect(() => {
		debounceGetUseCases()
	}, [searchBoxValue])

	function handleSearch(e) {
		setSearchBoxValue(e.target.value)
	}

	const handleCancel =()=> setSearchBoxValue("")

	/*=================================================
		Use Cases
	/================================================*/

	const [useCase, setUseCase] = useState({})
	const [useCases, setUseCases] = useState([])

	const tab = {
		enrolment: "Enrolment",
		principal: "Principals"
	}

	const defaultTabItems = [
		{
			label: <NavLink className="setting-btn" to="/use-cases/enrolment">{tab.enrolment}</NavLink>,
			path: "/use-cases/enrolment",
			active: true,
			options: ['enrolled', 'pending', 'not-enrolled'],
			id: tab.enrolment
		},
		{
			label: <NavLink className="setting-btn" to="/use-cases/principal">{tab.principal}</NavLink>,
			path: "/use-cases/principal",
			active: false,
			id: tab.principal
		}
	]

	const [tabsItems, setTabsItems] = useState(
		[defaultTabItems[0]].map((item) => ({
			...item,
			active: true,
		}))
	)

	const [chips, setChips] = useState([
		{ label: 'All', active: true, options: ['enrolled', 'pending', 'not-enrolled'], id: "use-cases-chip-items-all" },
		{ label: 'Enrolled', active: false, options: ['enrolled'], id: "use-cases-chip-items-enrolled" },
		{ label: 'Requested', active: false, options: ['pending'], id: "use-cases-chip-items-requested" },
	])

	useEffect(() => {
		if (profile?.organization?.organizationUseCases?.some(s => isAgentPrincipalEnabled(s.agentStatus))) {
			setTabsItems(defaultTabItems.map((item) => ({
				...item,
				active: item.path === currentPath,
			})));
		}
	}, [profile, currentPath])

	useEffect(() => {
		let activeItem = chips.find(item => item.active)

		const options = activeItem.options
		const filteredUseCases = items.filter(useCase => options.find(option => option === useCase.status))
		setUseCases(filteredUseCases)
	}, [items, tabsItems, chips])

	function selectChip(index) {
		const items = [...chips].map(item => ({ ...item, active: false }))
		items[index].active = true

		setChips(items)
	}

	const UseCasesList = () => {
		if (!Array.isArray(useCases)) {
			return ""
		}

		const list = useCases.map((item, index) => (
			<CardUseCase
				key={index}
				id={item.id}
				title={item.name}
				image={item.image}
				description={item.description}
				status={profile?.organization?.status !== statuses.approved ? profile?.organization?.status : item.status}
				noOfDataElements={item.dataElements.length}
				noOfParticipants={item.participants}
				agentStatus={item?.agentStatus}
				enableETR={item?.enableETR}
				role={userType}
			/>
		))

		return list
	}

	/*=================================================
		Principal + Popover
	/================================================*/

	const [filteredPrincipalData, setFilteredPrincipalData] = useState(undefined)
	const options = [{
		type: 'Bulk',
		text: 'Via Batch Upload'
	}, {
		type: 'Manual',
		text: 'Add Manually'
	}]

	const handlePrincipalMenuSubmit = (optionType) => {
		if (optionType === 'Manual') {
			goTo('/use-cases/principal/create')
		} else if (optionType === 'Bulk') {
			goTo('/use-cases/principal/create-bulk')
		}
	}


	return (
		<>
			{/* [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR. */}
			{/* <Breadcrumbs breadcrumbItems={breadcrumbsItems} /> */}
			<Breadcrumb items={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title" data-testid="page-title">
						Use Cases
					</div>
				</div>

				<Box display="flex" justify="end">
					{currentPath === defaultTabItems?.[1]?.path && (
						<div style={{ display: "flex", gap: rem(8) }}>
							{filteredPrincipalData?.some(s => s.$selected) &&
								<Button ghost round maxWidth={140} text="Remove" submit={() =>
									showRemovePrincipal()
								} />
							}
							<PrincipalMenuWithOutsideClicked
								options={options}
								onClick={handlePrincipalMenuSubmit}
							/>
						</div>
					)}
				</Box>
			</div>

			<Tabs>
				{tabsItems.map((item, index) => (
					<Tab key={index} id={item.id} label={item.label} active={item.active} />
				))}
			</Tabs>

			{/* // Secondary Tabs */}
			<>
				{currentPath === "/use-cases/enrolment" && (
					<>
						<Box display="flex" align="center" justify="start" direction="row" gap={4} my={8}>
							{
								feature(featureConstant.NEW_DESIGN_SYSTEM) ? (
									<div style={{width:'334px'}}>
										<Search value={searchBoxValue || ""}  placeholder="Search" onChange={(e) => { handleSearch(e) }} onCancel={handleCancel} />
									</div>
								) : (

									<InputPrefixIcon
										maxWidth="334"
										placeholder="Search"
										icon={<IconSearch />}
										value={searchBoxValue || ""}
										onChange={(e) => { handleSearch(e) }}
									/>
								)
							}

							<Box display="flex" ml={6} gap={2}>
								{chips.map((item, index) => (
									<Button
										ascent
										round
										maxWidth={140}
										text={item.label}
										selected={item.active}
										key={item.id}
										submit={() => selectChip(index)}
										data-testid={item.id}
									/>
								))}
							</Box>
						</Box>

						<Grid columns={{ xs: '271px', sm: 'repeat(2, 271px)', md: 'repeat(3, 271px)', lg: 'repeat(4, 271px)', xl: 'repeat(5, 271px)' }}
							columnGap={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2 }}
							rowGap={0.1}
							w={1632} h={832}>
							<UseCasesList />
						</Grid>
					</>
				)}
				{currentPath === "/use-cases/principal" && (
					<PrincipalPage
						filteredPrincipalData={filteredPrincipalData}
						setFilteredPrincipalData={setFilteredPrincipalData}
						visibleRemovePrincipal={visibleRemovePrincipal}
						showRemovePrincipal={showRemovePrincipal}
						hideRemovePrincipal={hideRemovePrincipal}
					/>
				)}
			</>

			<Toast toastList={toastList} />
		</>
	)
}

export default Page
